/**
 * UI 设计节点 · 解析上游图片节点的界面风格
 */

import {
  UI_DESIGN_BUILTIN_STYLES,
  UI_DESIGN_STYLE_CUSTOM_ID,
  uiDesignBuiltinStyleHintIds
} from '@/utils/uiDesignNode'

const STYLE_HINT_ENUM = uiDesignBuiltinStyleHintIds() + '|custom'

export const UI_UPSTREAM_STYLE_VISION_PREFIX = `你是「AI 界面设计风格」提取专家。用户会提供一张或多张参考界面截图（可能是任意 App/网页），以及「待设计的新产品名称」。

你的任务：只提取可复用的 **AI 生图 UI 视觉风格规范**（Design System），用于给另一个完全不同的产品做界面——不是描述参考图里是什么产品。

【必须遵守】
- 只写视觉：配色、背景、卡片、圆角、阴影、字体气质、图标、按钮、信息密度、导航/Tab 的视觉形态
- 禁止写参考图所属产品的功能、页面名称、业务模块（例如参考图是电商，不要写「商品详情」「购物车」）
- 禁止要求照抄参考图里的文案与业务布局
- 输出应能让设计师把同一套视觉用在「待设计新产品」上

须包含：
1. 【AI设计风格名称】如「柔和渐变消费级」「深色霓虹科技」等
2. 【配色体系】主色、辅色、背景、表面色、强调色（尽量 #RRGGBB）
3. 【组件质感】按钮/输入框/卡片/图标/标签 的圆角、描边、阴影、玻璃/扁平
4. 【排版与密度】字号层级气质、留白、列表/宫格视觉
5. 【生图风格描述】一段可直接写入 UI 生图 prompt 的中英混合风格句（200 字内，强调视觉不强调业务）

看不清写「待确认」。最后一行单独输出 JSON（无 markdown 代码块）：
{"styleKeywords":"...","primaryColor":"#RRGGBB","secondaryColor":"#RRGGBB","builtinStyleHint":"${STYLE_HINT_ENUM}"}`

export const UI_UPSTREAM_STYLE_INFER_SYSTEM = `你是「AI 界面设计风格」提取专家。用户无法直接看图，但提供了参考图的 URL/上游图片描述，以及待设计的新产品名。

只输出可复用的视觉风格规范（配色 HEX、组件质感、排版密度、生图风格描述）。不要分析参考图是什么 App、有哪些业务页面。不要编造 logo。

最后一行单独输出 JSON：
{"styleKeywords":"...","primaryColor":"#RRGGBB","secondaryColor":"#RRGGBB","builtinStyleHint":"${STYLE_HINT_ENUM}"}`

/**
 * 构建风格解析时的用户侧说明（强调：风格来自参考图，产品另定）
 * @param {{ targetProductName?: string }} opts
 */
export function buildUiUpstreamStyleParseHints(opts) {
  const name = String((opts && opts.targetProductName) || '').trim()
  const lines = [
    '【任务】从参考图/参考描述中仅提取 AI 界面视觉风格（Design System），供后续给「另一款产品」出图使用。',
    '不要描述参考图的产品功能与页面结构。'
  ]
  if (name) {
    lines.push(
      `【待设计新产品】${name} —— 本次只解析风格；策划阶段将用该风格设计「${name}」的功能页面，禁止照搬参考图业务。`
    )
  }
  return lines.join('\n')
}

/**
 * 供 UI 设计专家使用的参考风格块
 * @param {string} guide
 * @param {string} productName
 */
export function formatReferenceStyleGuideBlock(guide, productName) {
  const g = String(guide || '').trim()
  if (!g) return ''
  const product = String(productName || '').trim() || '待设计产品'
  return [
    '【参考 AI 设计风格 · 仅约束视觉】',
    '以下由上游参考图解析。全局规范与各屏 prompt 的配色、组件、质感必须对齐此风格。',
    `所有界面功能、页面名称、占位文案必须服务于待设计产品「${product}」，禁止复刻参考图所属产品的业务与页面。`,
    '',
    g
  ].join('\n')
}

const HEX_RE = /#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/g

const BUILTIN_HINT_MAP = [
  { id: 'flatMinimal', keys: ['极简扁平', '扁平', 'flat', '纯色块'] },
  { id: 'softNeumorphism', keys: ['轻拟物', '软拟物'] },
  { id: 'neumorphism', keys: ['新拟态', 'neumorphism', '浮雕'] },
  { id: 'glassmorphism', keys: ['玻璃拟态', 'glass'] },
  { id: 'frostedBlur', keys: ['毛玻璃', '模糊', '磨砂玻璃', '雾面'] },
  { id: 'ui3dOps', keys: ['3d运营', '3d 运营', '立体运营'] },
  { id: 'illustration25d', keys: ['2.5d', '2.5D', '等距', '插画风'] },
  { id: 'kawaiiCartoon', keys: ['卡通', '萌系', '可爱', '糖果色'] },
  { id: 'dopamine', keys: ['多巴胺', '活泼', '撞色', '高饱和'] },
  { id: 'memphis', keys: ['孟菲斯', 'memphis', '几何潮流'] },
  { id: 'acidDesign', keys: ['酸性', 'acid', 'y2k', '镭射'] },
  { id: 'cyberTech', keys: ['赛博', '机能', 'cyber', '霓虹', 'hud'] },
  { id: 'darkDeep', keys: ['暗黑', '深邃', '深色模式', 'oled'] },
  { id: 'guochao', keys: ['国潮', '国风', '中式', '祥云'] },
  { id: 'retroNostalgia', keys: ['复古', '怀旧', '做旧', '牛皮纸'] },
  { id: 'luxuryPremium', keys: ['轻奢', '高级', '奢华', '香槟金'] },
  { id: 'wabiSabi', keys: ['侘寂', '日式', 'wabi'] },
  { id: 'koreanFresh', keys: ['韩式', '清新', 'ins风'] },
  { id: 'matteFrost', keys: ['磨砂', '哑光', '莫兰迪'] },
  { id: 'lineSketch', keys: ['线稿', '手绘', '线条极简'] }
]

function normalizeHex(raw) {
  const s = String(raw || '').trim()
  if (!/^#[0-9a-fA-F]{3,6}$/.test(s)) return ''
  if (s.length === 4) {
    const r = s[1]
    const g = s[2]
    const b = s[3]
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase()
  }
  return s.toUpperCase()
}

function extractJsonTail(text) {
  const raw = String(text || '').trim()
  const lines = raw.split('\n').map(l => l.trim()).filter(Boolean)
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]
    if (!line.startsWith('{') || !line.endsWith('}')) continue
    try {
      return JSON.parse(line)
    } catch (_) {
      /* continue */
    }
  }
  const m = raw.match(/\{[\s\S]*"styleKeywords"[\s\S]*\}\s*$/)
  if (m) {
    try {
      return JSON.parse(m[0])
    } catch (_) {
      return null
    }
  }
  return null
}

function extractSection(text, title) {
  const re = new RegExp(`【${title}】\\s*([^【]+)`, 'i')
  const m = String(text || '').match(re)
  return m ? String(m[1]).trim() : ''
}

/**
 * @param {string} text
 * @returns {{ styleKeywords: string, primaryColor: string, secondaryColor: string, builtinStyleId: string, analysisBody: string }}
 */
export function parseUiDesignStyleFromAnalysis(text) {
  const raw = String(text || '').trim()
  const json = extractJsonTail(raw)
  let analysisBody = raw
  if (json) {
    const idx = raw.lastIndexOf('{')
    if (idx > 0) analysisBody = raw.slice(0, idx).trim()
  }

  const hexes = []
  const seen = new Set()
  let hm
  const hexRe = new RegExp(HEX_RE.source, 'g')
  while ((hm = hexRe.exec(raw)) !== null) {
    const h = normalizeHex(hm[0])
    if (h && !seen.has(h)) {
      seen.add(h)
      hexes.push(h)
    }
  }

  let primary = normalizeHex(json && json.primaryColor)
  let secondary = normalizeHex(json && json.secondaryColor)
  if (!primary && hexes[0]) primary = hexes[0]
  if (!secondary && hexes[1]) secondary = hexes[1]

  let styleKeywords = json && json.styleKeywords ? String(json.styleKeywords).trim() : ''
  if (!styleKeywords) {
    styleKeywords =
      extractSection(raw, '生图风格描述') ||
      extractSection(raw, 'AI设计风格名称') ||
      extractSection(raw, '整体风格') ||
      analysisBody.slice(0, 400)
  }

  let builtinStyleId = json && json.builtinStyleHint
    ? String(json.builtinStyleHint).trim().toLowerCase()
    : ''
  builtinStyleId = suggestUiDesignBuiltinStyleId(
    `${builtinStyleId} ${styleKeywords} ${analysisBody}`
  )

  const validIds = new Set(UI_DESIGN_BUILTIN_STYLES.map(s => s.id))
  if (!validIds.has(builtinStyleId)) {
    builtinStyleId = UI_DESIGN_STYLE_CUSTOM_ID
  }

  return {
    styleKeywords,
    primaryColor: primary,
    secondaryColor: secondary,
    builtinStyleId,
    analysisBody: analysisBody || raw
  }
}

export function suggestUiDesignBuiltinStyleId(text) {
  const t = String(text || '').toLowerCase()
  for (const row of BUILTIN_HINT_MAP) {
    if (row.keys.some(k => t.includes(String(k).toLowerCase()))) {
      return row.id
    }
  }
  return UI_DESIGN_STYLE_CUSTOM_ID
}

/**
 * @param {{ imageUrls: string[], upstreamMeta: string, textHints?: string, chat: Function, model: string, baseUrl?: string }} opts
 */
export async function inferUiDesignStyleFromUpstreamText(opts) {
  const chat = opts && opts.chat
  const model = opts && opts.model ? String(opts.model) : 'deepseek-v4-flash'
  const urls = (opts && opts.imageUrls) || []
  const meta = String((opts && opts.upstreamMeta) || '').trim()
  const hints = String((opts && opts.textHints) || '').trim()
  const urlBlock =
    urls.length > 0
      ? `【已连接参考界面 ${urls.length} 张，出图时将传入 Seedream 对齐风格】\n${urls
          .map((u, i) => `${i + 1}. ${u}`)
          .join('\n')}`
      : ''
  const user = [hints, meta, urlBlock].filter(Boolean).join('\n\n') || '请根据 UI 设计需求推断界面视觉风格。'
  const raw = await chat(
    model,
    [
      { role: 'system', content: UI_UPSTREAM_STYLE_INFER_SYSTEM },
      { role: 'user', content: user }
    ],
    opts && opts.baseUrl
  )
  return String(raw || '').trim()
}
